import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseProxyQueryWithReauth, baseQueryWithErrorHandling } from "./API";
import { pageProps } from "../../helpers/pageProps";

const el: any = document.querySelector("#app");
export const { contactsClientId, apiBase, csrfToken } = pageProps;

export type ActiveUsersWidgetDataType = {
  yesterday: number;
  today: number;
  thisWeek: number;
  previousWeek: number;
};

export type InteractionsWidgetDataType = { remaining: number; used: number };

export type MessagingChannel = "sms" | "email" | "voice";

export type ChannelMessagingStats = {
  broadcast_uuid?: string;
  dispatched: number;
  delivered: number;
  failed: number;
  expired?: number;
  replies: number;
};

export type ComponentMessagingStats = {
  process_id: number;
  process_guid: string;
  process_started_at: string;
  project_id: number;
  project_uuid: string;
  project_label: string;
  dispatched: number;
  delivered: number;
  failed: number;
  replies: number;
  channels?: Record<MessagingChannel, ChannelMessagingStats>;
};

export type ComponentMessagingResponse = {
  components: ComponentMessagingStats[];
  total: number;
};

export type BroadcastMessagingStats = {
  Uuid: string;
  CreatedAt: string;
  Email: string;
  Label: string;
  Description: string;
  Reference: string;
  Dispatched: number;
  Delivered: number;
  Failed: number;
  Expired: number;
  Replies: number;
  channels?: Record<MessagingChannel, ChannelMessagingStats>;
};

export type BroadcastMessagingResponse = {
  broadcasts: BroadcastMessagingStats[];
  total: number;
};

// localhost:8000
export const widgetsAPI = createApi({
  reducerPath: "widgetsAPI",
  baseQuery: baseQueryWithErrorHandling({
    prepareHeaders: (headers: Headers): Headers => {
      if (typeof csrfToken == "string") {
        headers.set("X-CSRF-TOKEN", csrfToken);
      }
      headers.set("Accept", "application/json; charset=utf-8");
      headers.set("Content-Type", "application/json; charset=utf-8");
      return headers;
    },
  }),
  refetchOnMountOrArgChange: true,
  tagTypes: [],
  endpoints: (builder) => ({
    getActiveUsersWidgetData: builder.query<ActiveUsersWidgetDataType, void>({
      query: () => `/logic/active-users`,
    }),
    getMessagesReportingWidgetData: builder.query<
      any,
      {
        startDate: string;
        endDate: string;
        // showWhen:'processed';
        comms: "sms" | "email" | "voice";
        // messageDirection:
        // messageStatus:
        timeUnit: "hour" | "day" | "week" | "month";
        // unit:
      }
    >({
      query: (params) => ({
        url: `/reports/gateway/getGraphData`,
        params,
      }),
      // `?startDate=2025-03-02T12:00:00.000Z&endDate=2025-03-04T00:00:00.000Z&showWhen=processed&comms=[COMMS]&messageDirection=1-Way,2-Way&messageStatus=delivered,sent,expired,failed,pending,cancelled,unknown&timeUnit=[TIME_UNIT]&unit=billable`,
    }),
    getRecentActivityData: builder.query<any, void>({
      query: () => `/dashboard/activity_logs`,
    }),
  }),
});

//localhost:3100 proxy: "logic/workflows/api" | "contacts/v1" | "logic/reporting"
export const widgetsProxyAPI = createApi({
  reducerPath: "widgetsProxyAPI",
  baseQuery: baseProxyQueryWithReauth("logic/workflows/api"),
  refetchOnMountOrArgChange: true,
  tagTypes: [],
  endpoints: (builder) => ({
    getInteractionsWidgetData: builder.query<InteractionsWidgetDataType, void>({
      query: () => `stats/interactions-current-month`,
    }),
    getInboundCampaignMessagingData: builder.query<
      { summary: any[]; total: number },
      {
        search?: string;
        date_gte?: string;
        date_lte?: string;
        search_field?: ("sender" | "recipient" | "subject" | "campaign" | "content")[];
        handler_uuid?: string[];
        channel?: MessagingChannel[];
        order_by?: "created_at" | "channel" | "from" | "to";
        order?: "asc" | "desc";
        offset?: number;
        limit?: number;
      }
    >({
      query: (params) => ({
        url: `stats/inbound-summary`,
        params,
      }),
    }),
    getBoomLogicWFMessagingData: builder.query<
      ComponentMessagingResponse,
      {
        search?: string;
        project_uuid?: string[];
        process_guid?: string;
        date_gte?: string;
        date_lte?: string;
        page?: number;
        per_page?: number;
        order?: "asc" | "desc";
        order_by?:
          | "start_at"
          | "dispatched"
          | "delivered"
          | "failed"
          | "replies";
      }
    >({
      query: (params) => ({
        url: `stats/component-messaging`,
        params,
      }),
    }),
    getBroadcastMessagingReportData: builder.query<
      BroadcastMessagingResponse,
      {
        order_by?: string;
        order?: "asc" | "desc";
        search?: string;
        search_field?: ("label" | "description" | "reference")[];
        date_gte?: string;
        date_lte?: string;
      }
    >({
      query: (params) => ({
        url: `stats/broadcast-messaging`,
        params,
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetActiveUsersWidgetDataQuery,
  useGetMessagesReportingWidgetDataQuery,
  useGetRecentActivityDataQuery,
} = widgetsAPI;

export const {
  useGetInteractionsWidgetDataQuery,
  useGetBroadcastMessagingReportDataQuery,
  useGetInboundCampaignMessagingDataQuery,
  useGetBoomLogicWFMessagingDataQuery,
} = widgetsProxyAPI;
